import { usePageTitle } from "@/routes/__root";
import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/site/PageHero";
import { Section, SectionHeading } from "@/components/site/Section";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Loading, ProblemAlert } from "@/components/site/Feedback";
import { MessageCircle } from "lucide-react";
import { isMobile } from "@/lib/config";
import { openExternal } from "@/lib/external";
import { sourcingWhatsAppUrl } from "@/lib/site-data";


const steps = [
  ["Brief", "We agree your strategy, target regions, budget and return threshold."],
  ["Search", "The team screens listings, off-market stock and auction lots against your brief."],
  ["Underwrite", "Every candidate is run through the Analyzer and stress-tested by hand."],
  ["Negotiate", "We handle the offer, the agent and the terms through to acceptance."],
  ["Package", "You receive the full deal pack: comparables, refurb schedule and power team."],
];

export function SourcingPage() {
  usePageTitle("Deal Sourcing");
  const fees = useQuery({ queryKey: ["deal-sourcing", "fees"], queryFn: api.dealSourcing.fees });
  return (
    <>
      <PageHero
        eyebrow="Deal sourcing"
        title="A portfolio built without the legwork"
        subtitle="For investors with capital and no time. We source, underwrite and negotiate; you review the pack and decide."
      >
        <Button asChild variant="gold" size="xl">
          {/* WhatsApp chat with the sourcing team (the mobile app opens it through the host). */}
          <a
            href={sourcingWhatsAppUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (isMobile) {
                e.preventDefault();
                openExternal(sourcingWhatsAppUrl);
              }
            }}
          >
            <MessageCircle />
            Request a sourcing call
          </a>
        </Button>
      </PageHero>

      <Section>
        <SectionHeading eyebrow="How it works" title="Five stages, one outcome" />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-5">
          {steps.map(([t, b], i) => (
            <div key={t} className="rounded-lg border border-border bg-card p-6 shadow-card">
              <span className="font-display text-3xl text-gold">{`0${i + 1}`}</span>
              <h3 className="mt-3 text-lg">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section ink>
        <SectionHeading
          light
          eyebrow="Fee scale"
          title="Transparent, capital-based pricing"
          subtitle="Fees are payable upon formal offer acceptance or agreed completion terms, based on total project capital."
        />
        {fees.isPending ? (
          <Loading className="text-ink-foreground/70" label="Loading the fee scale…" />
        ) : fees.isError ? (
          <ProblemAlert error={fees.error} className="mt-12 bg-ink-foreground/5" />
        ) : (
          <div className="mt-12 overflow-x-auto rounded-lg border border-ink-foreground/15">
            <table className="w-full min-w-[32rem] text-left text-sm">
              <thead className="bg-ink-foreground/5 text-ink-foreground/60">
                <tr>
                  <th className="px-6 py-4 font-medium">Total investment capital</th>
                  <th className="px-6 py-4 font-medium">Sourcing fee</th>
                  <th className="px-6 py-4 font-medium">Max fee example</th>
                </tr>
              </thead>
              <tbody className="text-ink-foreground/85">
                {[...fees.data]
                  .sort((a, b) => a.order - b.order)
                  .map((f) => (
                    <tr key={f.id} className="border-t border-ink-foreground/10">
                      <td className="px-6 py-4">{f.totalInvestmentCapital}</td>
                      <td className="px-6 py-4 font-medium text-gold">{f.sourcingFee}</td>
                      <td className="px-6 py-4">{f.maxFee}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="mt-5 text-xs text-ink-foreground/50">
          Priority, Bespoke and VIP sourcing tiers are unlocked through the Silver, Gold and
          Platinum schemes respectively.
        </p>
      </Section>
    </>
  );
}
